/**
 * Custom hook for managing inline editing state and operations
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Hold, HoldList } from 'api-client/models';
import { useAppContext } from '@/context/AppContext';
import type { TicketRevenueItem } from '../types/TicketRevenueItem';
import type { ExpenseItem } from '../types/ExpenseItem';
import { isHoldDetailSelectable } from '../utils/holdSelection';
import { normalizeExpenseItems } from '../utils/expenseItems';

type HoldDetails = Omit<Hold, 'expenseItems'> & { expenseItems?: ExpenseItem[] };

// Type for form data that can contain various hold fields
type HoldFormData = {
  loadInTime?: string | null;
  soundcheckTime?: string | null;
  doorsTime?: string | null;
  showTime?: string | null;
  endTime?: string | null;
  showCapacity?: number | null;
  attendance?: number | null;
  ticketRevenueItems?: TicketRevenueItem[];
  barRevenue?: string | null;
  otherRevenue?: string | null;
  expenseItems?: ExpenseItem[];
  [key: string]: string | number | null | TicketRevenueItem[] | ExpenseItem[] | undefined;
};

interface UseInlineEditingResult {
  editingSection: string | null;
  editFormData: HoldFormData;
  fullHoldDetails: Record<string, HoldDetails>;
  startEditing: (section: string, holdId: string) => Promise<void>;
  cancelEditing: () => void;
  saveChanges: (
    section: string,
    showId: string,
    selectedHoldForDetails: HoldList | null,
    onSuccess: () => Promise<void>,
  ) => Promise<void>;
  updateFormField: (field: string, value: string | number | null | TicketRevenueItem[] | ExpenseItem[]) => void;
  fetchFullHoldDetails: (holdId: string) => Promise<HoldDetails | null>;
  setEditFormData: (data: HoldFormData) => void;
}

/**
 * Hook to manage inline editing of hold details
 * Handles fetching full hold details, editing state, and saving changes
 */
export const useInlineEditing = (
  setInfoMessage: (message: string | null) => void,
  setError: (error: string | null) => void,
  scope: { showId?: string; holds: HoldList[]; selectedHoldId?: string },
): UseInlineEditingResult => {
  const { apiClients } = useAppContext();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const cacheScope = useRef({ showId: scope.showId, holds: scope.holds });
  if (cacheScope.current.showId !== scope.showId || cacheScope.current.holds !== scope.holds) {
    cacheScope.current = { showId: scope.showId, holds: scope.holds };
  }
  const version = cacheScope.current;
  const editScope = useRef({ showId: scope.showId, holdId: scope.selectedHoldId });
  if (editScope.current.showId !== scope.showId || editScope.current.holdId !== scope.selectedHoldId) {
    editScope.current = { showId: scope.showId, holdId: scope.selectedHoldId };
  }
  const editVersion = editScope.current;
  const [scrollRestore, setScrollRestore] = useState<{
    version: typeof editVersion; y: number; waitForDetails: boolean;
  } | null>(null);
  const [detailCompletion, setDetailCompletion] = useState<{ version: typeof version; holdId: string } | null>(null);
  const editRequest = useRef(0);
  const detailRequests = useRef(new Map<string, number>());
  const [cache, setCache] = useState<{ version: typeof version; details: Record<string, HoldDetails> }>({
    version, details: {},
  });
  const [editing, setEditing] = useState<{
    version: typeof editVersion; section: string | null; holdId: string | null; form: HoldFormData;
  }>({ version: editVersion, section: null, holdId: null, form: {} });
  const fullHoldDetails = cache.version === version ? cache.details : {};
  const editingSection = editing.version === editVersion ? editing.section : null;
  const editFormData = editing.version === editVersion ? editing.form : {};
  useLayoutEffect(() => {
    if (scrollRestore?.version !== editVersion || !mounted.current) return;
    if (scrollRestore.waitForDetails && !(cache.version === version && cache.details[scope.selectedHoldId ?? ''])
      && !(detailCompletion?.version === version && detailCompletion.holdId === scope.selectedHoldId)) return;
    window.scrollTo(0, scrollRestore.y);
    setScrollRestore(null);
  }, [scrollRestore, editVersion, cache, version, detailCompletion, scope.selectedHoldId]);

  useEffect(() => { setError(null); }, [scope.showId, setError]);

  const fetchFullHoldDetails = useCallback(async (holdId: string): Promise<HoldDetails | null> => {
    if (!mounted.current || cacheScope.current !== version) return null;
    if (!version.holds.some((hold) => hold.id === holdId && isHoldDetailSelectable(hold))) {
      setError('Selected date is no longer available.');
      return null;
    }
    const request = (detailRequests.current.get(holdId) ?? 0) + 1;
    detailRequests.current.set(holdId, request);
    const isCurrent = () => mounted.current && cacheScope.current === version
      && detailRequests.current.get(holdId) === request;
    try {
      const response = await apiClients.holds.holdsRetrieve({ id: holdId });
      if (!isCurrent()) return null;
      if (response.id !== holdId || response.show !== version.showId) {
        throw new Error('Hold detail response does not belong to the current show/date.');
      }
      const { expenseItems, ...fields } = response;
      const holdDetails: HoldDetails = expenseItems === undefined ? fields
        : { ...fields, expenseItems: normalizeExpenseItems(expenseItems) };
      setCache((previous) => ({
        version,
        details: { ...(previous.version === version ? previous.details : {}), [holdId]: holdDetails },
      }));
      return holdDetails;
    } catch (error) {
      if (isCurrent()) {
        setError(error instanceof Error ? `Failed to load hold details: ${error.message}` : 'Failed to load hold details');
      }
      return null;
    } finally {
      if (isCurrent()) setDetailCompletion({ version, holdId });
    }
  }, [apiClients.holds, version, setError]);

  /**
   * Start editing a section and load the form data
   */
  const startEditing = async (section: string, holdId: string) => {
    if (!mounted.current || editScope.current !== editVersion) return;
    if (!['timing', 'ticketing', 'financial'].includes(section)) {
      setError('Unknown editing section.');
      return;
    }
    const request = ++editRequest.current;
    setError(null);
    const holdDetails = await fetchFullHoldDetails(holdId);
    if (!holdDetails || editScope.current !== editVersion || editRequest.current !== request) return;
    let form: HoldFormData;
    if (section === 'timing') {
      form = {
        loadInTime: holdDetails.loadInTime,
        soundcheckTime: holdDetails.soundcheckTime,
        doorsTime: holdDetails.doorsTime,
        showTime: holdDetails.showTime,
        endTime: holdDetails.endTime,
      };
    } else if (section === 'ticketing') {
      form = {
        showCapacity: holdDetails.showCapacity,
        attendance: holdDetails.attendance,
      };
    } else {
      // Ensure arrays are properly initialized
      const ticketRevenueItems = Array.isArray(holdDetails.ticketRevenueItems)
        ? holdDetails.ticketRevenueItems
        : [];
      const expenseItems = Array.isArray(holdDetails.expenseItems)
        ? holdDetails.expenseItems
        : [];

      form = {
        ticketRevenueItems,
        barRevenue: holdDetails.barRevenue,
        otherRevenue: holdDetails.otherRevenue,
        expenseItems,
      };
    }
    setEditing({ version: editVersion, section, holdId, form });
  };

  /**
   * Cancel editing and clear form data
   */
  const cancelEditing = () => {
    if (!mounted.current || editScope.current !== editVersion) return;
    ++editRequest.current;
    setEditing({ version: editVersion, section: null, holdId: null, form: {} });
  };

  /**
   * Update a single form field
   */
  const updateFormField = (field: string, value: string | number | null | TicketRevenueItem[] | ExpenseItem[]) => {
    if (!mounted.current || editScope.current !== editVersion) return;
    setEditing((previous) => ({
      ...previous,
      form: { ...previous.form, [field]: value },
    }));
  };

  /**
   * Save changes to the hold
   */
  const saveChanges = async (
    section: string,
    showId: string,
    selectedHoldForDetails: HoldList | null,
    onSuccess: () => Promise<void>,
  ) => {
    if (!mounted.current) return;
    if (editScope.current !== editVersion || cacheScope.current !== version
      || !selectedHoldForDetails || showId !== scope.showId || editing.version !== editVersion
      || editing.holdId !== selectedHoldForDetails.id || section !== editingSection
      || !scope.holds.some((hold) => hold.id === selectedHoldForDetails.id && isHoldDetailSelectable(hold))) {
      setError('Select a current date and editing section before saving.');
      return;
    }
    const holdIdToUpdate = selectedHoldForDetails.id;
    const scrollPosition = window.scrollY;
    let completed = false;
    const request = editRequest.current;
    const isCurrentEdit = () => mounted.current && editScope.current === editVersion && editRequest.current === request;
    let saved = false;
    setInfoMessage(null);
    setError(null);
    try {
      await apiClients.holds.holdsPartialUpdate({
        id: holdIdToUpdate,
        patchedHoldCreateUpdate: editFormData,
      });
      saved = true;
      if (!mounted.current || editScope.current !== editVersion) return;
      setCache((previous) => {
        const details = { ...(previous.version === version ? previous.details : {}) };
        delete details[holdIdToUpdate];
        return { version, details };
      });
      const refreshed = await fetchFullHoldDetails(holdIdToUpdate);
      const failures: string[] = [];
      if (!refreshed) failures.push('date details');
      try {
        await onSuccess();
      } catch {
        failures.push('show data');
      }
      if (!isCurrentEdit()) return;
      if (failures.length > 0) throw new Error(`Could not refresh ${failures.join(' and ')}.`);
      cancelEditing();
      setInfoMessage('Changes saved successfully');
      completed = true;
    } catch (error) {
      if (isCurrentEdit()) {
        const reason = error instanceof Error ? error.message : 'Please try again.';
        setError(saved
          ? `Changes were saved, but refresh failed. ${reason} Reload to verify current values.`
          : `Failed to save changes. ${reason}`);
      }
    } finally {
      if (mounted.current && editScope.current === editVersion) {
        setScrollRestore({ version: editVersion, y: scrollPosition, waitForDetails: completed });
      }
    }
  };

  return {
    editingSection,
    editFormData,
    fullHoldDetails,
    startEditing,
    cancelEditing,
    saveChanges,
    updateFormField,
    fetchFullHoldDetails,
    setEditFormData: (form) => {
      if (mounted.current && editScope.current === editVersion) setEditing((previous) => ({ ...previous, form }));
    },
  };
};
